import { FlexGroup, FlexItem, Form } from '@antoree/ant-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { memo, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessage, FormattedMessage } from 'react-intl';
import { FORM_DEFAULT_VALUES, FORM_TEST_RESULT_ID, SCHEMA } from '../constant';
import FormSectionTestResult from './FormSectionTestResult';
import Td from './Td';
import { TestResult } from 'services/session/type';

export type FormTestResultProps = {
  onChangeTotalScore?: (value: number | string) => void;
  isAdult?: boolean;
  mutate: any;
  mutateEdit: any;
  id: number;
  testId: number;
  link: string;
  isCourseUnit: boolean;
  isCourseTest: boolean;
  testType: number;
  testResult: TestResult | undefined;
  isVisibleEdit: boolean;
  autoFillData?: Partial<IFormData> | null;
};
export type IFormData = {
  fluency1: string;
  fluency1Score: string | number;
  fluency2: string;
  fluency2Score: string | number;
  fluency3: string;
  fluency3Score: string | number;
  lexical1: string;
  lexical1Score: string | number;
  lexical2: string;
  lexical2Score: string | number;
  lexical3: string;
  lexical3Score: string | number;
  grammatical1: string;
  grammatical1Score: string | number;
  grammatical2: string;
  grammatical2Score: string | number;
  grammatical3: string;
  grammatical3Score: string | number;
  pronunciation1: string;
  pronunciation1Score: string | number;
  pronunciation2: string;
  pronunciation2Score: string | number;
  pronunciation3: string;
  pronunciation3Score: string | number;
  personalCriteria1: string;
  personalCriteria2: string;
  comment1: string;
  comment2: string;
  comment3: string;
  sumScore: string;
  testType: number;
  link: string;
};
const FormTestResult: React.FC<FormTestResultProps> = ({
  onChangeTotalScore = () => {},
  isAdult = false,
  mutate,
  id,
  isCourseUnit,
  link,
  isCourseTest,
  testType,
  testResult,
  isVisibleEdit,
  mutateEdit,
  testId,
  autoFillData,
}) => {
  const {
    control,
    formState: { errors },
    watch,
    handleSubmit,
    getValues,
    reset,
    setValue,
  } = useForm<IFormData>({
    mode: 'onChange',
    resolver: yupResolver(SCHEMA(isAdult)),
    defaultValues: FORM_DEFAULT_VALUES(isAdult),
  });

  const onFormSubmit = (data: IFormData) => {
    if (isVisibleEdit) {
      mutateEdit({
        id,
        ...data,
        link,
        testType,
        isCourseUnit: isCourseTest ? undefined : isCourseUnit ? 1 : 0,
        testId: testId,
      });

      return;
    }
    mutate({
      id,
      ...data,
      link,
      testType,
      isCourseUnit: isCourseTest ? undefined : isCourseUnit ? 1 : 0,
    });
  };

  const scores = [
    parseInt(String(watch('fluency1Score')), 10),
    parseInt(String(watch('fluency2Score')), 10),
    parseInt(String(watch('fluency3Score')), 10),
    parseInt(String(watch('lexical1Score')), 10),
    parseInt(String(watch('lexical2Score')), 10),
    parseInt(String(watch('lexical3Score')), 10),
    parseInt(String(watch('grammatical1Score')), 10),
    parseInt(String(watch('grammatical2Score')), 10),
    parseInt(String(watch('grammatical3Score')), 10),
    parseInt(String(watch('pronunciation1Score')), 10),
    parseInt(String(watch('pronunciation2Score')), 10),
    parseInt(String(watch('pronunciation3Score')), 10),
  ];
  useMemo(() => {
    const totalScore = scores.reduce((acc: number, val: number) => {
      const a = acc || 0;
      const b = val || 0;
      return a + b;
    }, 0);
    if (isAdult) {
      const realScore = totalScore / 12;
      const integralPart = realScore.toFixed(2);
      onChangeTotalScore(integralPart);
    } else {
      onChangeTotalScore(totalScore);
    }
  }, [...scores, isAdult]);

  useEffect(() => {
    if (isVisibleEdit) {
      if (testType !== testResult?.testType) {
        reset(FORM_DEFAULT_VALUES(isAdult));
        return;
      }
      reset({
        fluency1: testResult?.testFluency1 || '',
        fluency1Score: testResult?.testFluency1Score || 0,
        fluency2: testResult?.testFluency2 || '',
        fluency2Score: testResult?.testFluency2Score || 0,
        fluency3: testResult?.testFluency3 || '',
        fluency3Score: testResult?.testFluency3Score || 0,
        lexical1: testResult?.testLexical1 || '',
        lexical1Score: testResult?.testLexical1Score || 0,
        lexical2: testResult?.testLexical2 || '',
        lexical2Score: testResult?.testLexical2Score || 0,
        lexical3: testResult?.testLexical3 || '',
        lexical3Score: testResult?.testLexical3Score || 0,
        grammatical1: testResult?.testGrammatical1 || '',
        grammatical1Score: testResult?.testGrammatical1Score || 0,
        grammatical2: testResult?.testGrammatical2 || '',
        grammatical2Score: testResult?.testGrammatical2Score || 0,
        grammatical3: testResult?.testGrammatical3 || '',
        grammatical3Score: testResult?.testGrammatical3Score || 0,
        pronunciation1: testResult?.testPronunciation1 || '',
        pronunciation1Score: testResult?.testPronunciation1Score || 0,
        pronunciation2: testResult?.testPronunciation2 || '',
        pronunciation2Score: testResult?.testPronunciation2Score || 0,
        pronunciation3: testResult?.testPronunciation3 || '',
        pronunciation3Score: testResult?.testPronunciation3Score || 0,
        personalCriteria1: testResult?.testPersonalCriteria1 || '',
        personalCriteria2: testResult?.testPersonalCriteria2 || '',
        comment1: testResult?.testComment1 || '',
        comment2: testResult?.testComment2 || '',
        comment3: testResult?.testComment3 || '',
        sumScore: testResult?.sumScore ? `${testResult?.sumScore}` : undefined,
        testType: testResult?.testType,
        link: testResult?.link,
      });
    }
  }, [testResult, testType]);

  useEffect(() => {
    if (!autoFillData) return;
    Object.entries(autoFillData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        setValue(key as keyof IFormData, value as any, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
  }, [autoFillData, setValue]);

  return (
    <FlexGroup>
      <FlexItem>
        <Form
          id={FORM_TEST_RESULT_ID}
          component="form"
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <table className="table-fixed">
            <thead>
              <tr>
                <Td colSpan={2} width="500px">
                  <FormattedMessage defaultMessage="Criteria" />
                </Td>
                <Td width="96px">
                  <>
                    <FormattedMessage defaultMessage="Score" />
                    <p>{isAdult ? `0 - 9` : `0 - 3`}</p>
                  </>
                </Td>
                <Td colSpan={2} width="400px">
                  <FormattedMessage defaultMessage="Details" />
                </Td>
              </tr>
            </thead>
            <tbody>
              <FormSectionTestResult
                control={control}
                rowDataList={[
                  {
                    mainLabel: defineMessage({
                      defaultMessage:
                        '1. Fluency and Coherence/ Phản xạ và độ lưu loát',
                    }),
                    label: defineMessage({
                      defaultMessage:
                        'Avoid silence or hesitation / Tránh được sự im lặng và do dự khi nói',
                    }),
                    collections: [
                      {
                        fieldName: 'fluency1Score',
                        isError: !!errors.fluency1Score,
                        message: errors.fluency1Score?.message,
                      },
                      {
                        fieldName: 'fluency1',
                        isError: !!errors.fluency1,
                        message: errors.fluency1?.message,
                        isTextArea: true,
                      },
                    ],
                    rowSpan: 3,
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage:
                        'Speak at length on each topic/ Nói được một đoạn dài trong mỗi chủ đề',
                    }),
                    collections: [
                      {
                        fieldName: 'fluency2Score',
                        isError: !!errors.fluency2Score,
                        message: errors.fluency2Score?.message,
                      },
                      {
                        fieldName: 'fluency2',
                        isError: !!errors.fluency2,
                        message: errors.fluency2?.message,
                        isTextArea: true,
                      },
                    ],
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage:
                        'Use correct words to express ideas/ Sử dụng từ hợp lý để diễn đạt ý tưởng',
                    }),
                    collections: [
                      {
                        fieldName: 'fluency3Score',
                        isError: !!errors.fluency3Score,
                        message: errors.fluency3Score?.message,
                      },
                      {
                        fieldName: 'fluency3',
                        isError: !!errors.fluency3,
                        message: errors.fluency3?.message,
                        isTextArea: true,
                      },
                    ],
                    display: true,
                  },
                ]}
              />
              <FormSectionTestResult
                control={control}
                rowDataList={[
                  {
                    mainLabel: defineMessage({
                      defaultMessage: '2. Lexical Resources/ Từ vựng',
                    }),
                    label: defineMessage({
                      defaultMessage:
                        'Use a wide range of vocabulary/ Sử dụng từ vựng đa dạng',
                    }),
                    collections: [
                      {
                        fieldName: 'lexical1Score',
                        isError: !!errors.lexical1Score,
                        message: errors.lexical1Score?.message,
                      },
                      {
                        fieldName: 'lexical1',
                        isError: !!errors.lexical1,
                        message: errors.lexical1?.message,
                        isTextArea: true,
                      },
                    ],
                    rowSpan: isAdult ? 3 : 2,
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage:
                        'Use phrasal verbs and collocation/ Sử dụng cụm động từ và kết hợp từ',
                    }),
                    collections: [
                      {
                        fieldName: 'lexical2Score',
                        isError: !!errors.lexical2Score,
                        message: errors.lexical2Score?.message,
                      },
                      {
                        fieldName: 'lexical2',
                        isError: !!errors.lexical2,
                        message: errors.lexical2?.message,
                        isTextArea: true,
                      },
                    ],
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage:
                        'Paraphrase/ Diễn đạt ý theo một cách khác',
                    }),
                    collections: [
                      {
                        fieldName: 'lexical3Score',
                        isError: !!errors.lexical3Score,
                        message: errors.lexical3Score?.message,
                      },
                      {
                        fieldName: 'lexical3',
                        isError: !!errors.lexical3,
                        message: errors.lexical3?.message,
                        isTextArea: true,
                      },
                    ],
                    display: isAdult,
                  },
                ]}
              />
              <FormSectionTestResult
                control={control}
                rowDataList={[
                  {
                    mainLabel: defineMessage({
                      defaultMessage:
                        '3. Grammatical Range and Accuracy/ Ngữ pháp và độ chính xác',
                    }),
                    label: defineMessage({
                      defaultMessage:
                        'Speak in complex sentences/ Sử dụng các mẫu câu phức tạp khi nói',
                    }),
                    collections: [
                      {
                        fieldName: 'grammatical1Score',
                        isError: !!errors.grammatical1Score,
                        message: errors.grammatical1Score?.message,
                      },
                      {
                        fieldName: 'grammatical1',
                        isError: !!errors.grammatical1,
                        message: errors.grammatical1?.message,
                        isTextArea: true,
                      },
                    ],
                    rowSpan: 3,
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage:
                        'Use a variety of grammatical forms/ Sử dụng đa dạng các loại ngữ pháp',
                    }),
                    collections: [
                      {
                        fieldName: 'grammatical2Score',
                        isError: !!errors.grammatical2Score,
                        message: errors.grammatical2Score?.message,
                      },
                      {
                        fieldName: 'grammatical2',
                        isError: !!errors.grammatical2,
                        message: errors.grammatical2?.message,
                        isTextArea: true,
                      },
                    ],
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage:
                        'Avoid grammatical mistakes/ Tránh được lỗi sai',
                    }),
                    collections: [
                      {
                        fieldName: 'grammatical3Score',
                        isError: !!errors.grammatical3Score,
                        message: errors.grammatical3Score?.message,
                      },
                      {
                        fieldName: 'grammatical3',
                        isError: !!errors.grammatical3,
                        message: errors.grammatical3?.message,
                        isTextArea: true,
                      },
                    ],
                    display: true,
                  },
                ]}
              />
              <FormSectionTestResult
                control={control}
                rowDataList={[
                  {
                    mainLabel: defineMessage({
                      defaultMessage: '4. Pronunciation/ Phát âm',
                    }),
                    label: defineMessage({
                      defaultMessage:
                        'Pronounce words accurately/ Phát âm chính xác các từ khi nói',
                    }),
                    collections: [
                      {
                        fieldName: 'pronunciation1Score',
                        isError: !!errors.pronunciation1Score,
                        message: errors.pronunciation1Score?.message,
                      },
                      {
                        fieldName: 'pronunciation1',
                        isError: !!errors.pronunciation1,
                        message: errors.pronunciation1?.message,
                        isTextArea: true,
                      },
                    ],
                    rowSpan: isAdult ? 3 : 2,
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage: 'Join sounds together/ Biết nối âm',
                    }),
                    collections: [
                      {
                        fieldName: 'pronunciation2Score',
                        isError: !!errors.pronunciation2Score,
                        message: errors.pronunciation2Score?.message,
                      },
                      {
                        fieldName: 'pronunciation2',
                        isError: !!errors.pronunciation2,
                        message: errors.pronunciation2?.message,
                        isTextArea: true,
                      },
                    ],
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage: 'Vary intonation/ Có ngữ điệu rõ ràng',
                    }),
                    collections: [
                      {
                        fieldName: 'pronunciation3Score',
                        isError: !!errors.pronunciation3Score,
                        message: errors.pronunciation3Score?.message,
                      },
                      {
                        fieldName: 'pronunciation3',
                        isError: !!errors.pronunciation3,
                        message: errors.pronunciation3?.message,
                        isTextArea: true,
                      },
                    ],
                    display: isAdult,
                  },
                ]}
              />
              <FormSectionTestResult
                control={control}
                rowDataList={[
                  {
                    mainLabel: defineMessage({
                      defaultMessage:
                        '5. Personal criteria/ Yếu tố cá nhân hóa',
                    }),
                    label: defineMessage({
                      defaultMessage:
                        'Attitude towards English learning/ Thái độ với tiếng Anh',
                    }),
                    collections: [
                      {
                        fieldName: 'personalCriteria1',
                        isError: !!errors.personalCriteria1,
                        message: errors.personalCriteria1?.message,
                        isTextArea: true,
                        colSpan: 2,
                      },
                    ],
                    rowSpan: 2,
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage:
                        'Second Language Acquisition/ Khả năng hấp thu ngôn ngữ',
                    }),
                    collections: [
                      {
                        fieldName: 'personalCriteria2',
                        isError: !!errors.personalCriteria2,
                        message: errors.personalCriteria2?.message,
                        isTextArea: true,
                        colSpan: 2,
                      },
                    ],
                    display: true,
                  },
                ]}
              />
              <FormSectionTestResult
                control={control}
                rowDataList={[
                  {
                    mainLabel: defineMessage({
                      defaultMessage: '6. Comment/Nhận xét',
                    }),
                    label: defineMessage({
                      defaultMessage: 'Strengths/ Thế mạnh',
                    }),
                    collections: [
                      {
                        fieldName: 'comment1',
                        isError: !!errors.comment1,
                        message: errors.comment1?.message,
                        isTextArea: true,
                        colSpan: 2,
                      },
                    ],
                    rowSpan: 3,
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage: 'Weakness/ Điểm yếu',
                    }),
                    collections: [
                      {
                        fieldName: 'comment2',
                        isError: !!errors.comment2,
                        message: errors.comment2?.message,
                        isTextArea: true,
                        colSpan: 2,
                      },
                    ],
                    display: true,
                  },
                  {
                    label: defineMessage({
                      defaultMessage: 'Recommendation/ Đề xuất',
                    }),
                    collections: [
                      {
                        fieldName: 'comment3',
                        isError: !!errors.comment3,
                        message: errors.comment3?.message,
                        isTextArea: true,
                        colSpan: 2,
                      },
                    ],
                    display: true,
                  },
                ]}
              />
            </tbody>
          </table>
        </Form>
      </FlexItem>
    </FlexGroup>
  );
};

export default memo(FormTestResult);
